/**
 * 将采购套数规范为不小于 1 的整数。
 * @param {number|string} value 原始套数
 * @returns {number} 安全采购套数
 */
export const normalizePurchaseSets = (value) => {
  const parsed = Math.floor(Number(value));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

/**
 * 仅在采购套数属于当前 BOM 时返回该值，否则回退为 1 套。
 * @param {number|string} value 采购套数
 * @param {string} valueBomId 采购套数所属 BOM ID
 * @param {string} currentBomId 当前 BOM ID
 * @returns {number} 当前 BOM 可安全使用的采购套数
 */
export const resolvePurchaseSetsForBom = (value, valueBomId, currentBomId) => {
  if (!currentBomId || String(valueBomId || "") !== String(currentBomId)) return 1;
  return normalizePurchaseSets(value);
};

/**
 * 获取当前 BOM 行实际采用的订购量，兼容缺少 orderQty 的旧数据。
 * @param {Object} item BOM 明细
 * @returns {number} 当前订购量
 */
export const getBomOrderQty = (item = {}) => {
  const normalizedOrderQty = Number(item.normalized?.orderQty || 0);
  if (Number.isFinite(normalizedOrderQty) && normalizedOrderQty > 0) return normalizedOrderQty;

  const quantity = Number(item.quantity || 0);
  if (Number.isFinite(quantity) && quantity > 0) return quantity;

  const unitQty = Number(item.normalized?.unitQty || item.original?.unitQty || 1);
  return Number.isFinite(unitQty) && unitQty > 0 ? unitQty : 1;
};

/**
 * 构建 Apply Sets 批量覆盖前的逐行数量预览。
 * @param {Object[]} items BOM 明细
 * @param {number|string} purchaseSets 采购套数
 * @param {Object[]} livePatches 表格尚未反映到页面状态的实时补丁
 * @returns {{sets: number, rows: Object[], changedCount: number}} 套数应用预览
 */
export const buildPurchaseSetsPreview = (items = [], purchaseSets, livePatches = []) => {
  const sets = normalizePurchaseSets(purchaseSets);
  const patchMap = new Map(livePatches.map((patch) => [String(patch.itemId), patch]));
  const rows = items.map((item) => {
    const liveNormalized = patchMap.get(String(item._id))?.normalized || {};
    const normalized = { ...(item.normalized || {}), ...liveNormalized };
    const parsedUnitQty = Number(normalized.unitQty || item.original?.unitQty || 1);
    const parsedSpareQty = Number(normalized.productionSurplusQty || 0);
    const unitQty = Number.isFinite(parsedUnitQty) && parsedUnitQty > 0 ? parsedUnitQty : 1;
    const spareQty = Number.isFinite(parsedSpareQty) && parsedSpareQty > 0 ? parsedSpareQty : 0;
    const currentOrderQty = getBomOrderQty(item);
    const nextOrderQty = unitQty * sets + spareQty;

    return {
      itemId: item._id,
      lineNo: item.lineNo,
      partNumber: normalized.originalMpn
        || normalized.originalSupplierPartNo
        || item.productSnapshot?.CTYEA_Part_Number
        || "-",
      unitQty,
      spareQty,
      currentOrderQty,
      nextOrderQty,
      changed: currentOrderQty !== nextOrderQty,
    };
  });

  return {
    sets,
    rows,
    changedCount: rows.filter((row) => row.changed).length,
  };
};
